import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { BasicBackendService } from 'src/app/services/basic-backend.service';
import { CrisisTeamBackendService } from 'src/app/services/crisis-team-backend.service';
import { DashboardBackendService } from 'src/app/services/dashboard-backend.service';
import { GeneralBackendService } from 'src/app/services/general-backend.service';
import { HousingBackendService } from 'src/app/services/housing-backend.service';
import { HrBackendService } from 'src/app/services/hr-backend.service';
import { MyBackendService } from 'src/app/services/my-backend.service';
import { RouterService } from 'src/app/services/router.service';
import { UserBackendService } from 'src/app/services/user-backend.service';
import { Appointment } from '../models/appointment.model';
import { ApproverAssignment } from '../models/approver-assignment.model';
import { BaseResponse } from '../models/base-response.model';
import { CallingTreeAssignments } from '../models/calling-tree-assignments.model';
import { CallingTreeCoordinator } from '../models/calling-tree-coordinator.model';
import { Dependent } from '../models/dependent.model';
import { DropDownItem } from '../models/drop-down-item.model';
import { EmergencyContact } from '../models/emergency-contact.model';
import { EmergencyInfo } from '../models/emergency-info.model';
import { HousingUnit } from '../models/housing-unit.model';
import { Housing } from '../models/housing.model';
import { Insurance } from '../models/insurance.model';
import { Language } from '../models/language.model';
import { NgxsForm } from '../models/ngxs-form.model';
import { PersonnelStatus } from '../models/personnel-status.model';
import { Pet } from '../models/pet.model';
import { Residency } from '../models/residency.model';
import { TravelItinerary } from '../models/travel-itinerary.model';
import { UserDropDown } from '../models/user-drop-down.model';
import { UserPassport } from '../models/user-passport.model';
import { UserPhoneInfo } from '../models/user-phone-info.model';
import { UserQatarInfo } from '../models/user-qatar-info.model';
import { UserSearch, UserSearchResponse } from '../models/user-search.model';
import { UserSummary } from '../models/user-summary.model';
import { User } from '../models/user.model';
import { MathUtility } from '../utility/math.utility';
import { Logout } from './auth.action';
import { AuthStateModel } from './auth.model';
import { AuthState } from './auth.state';
import { StartLoading, StopLoading } from './main.action';
import {
	ActivateUser,
	ArchiveUser,
	ClearUser,
	ClearUserSearchResult,
	ConfirmMyRecord,
	GetApproverAssignments,
	GetAvailableHousingUnits,
	GetCallingTreeAssignments,
	GetCurrentAppointment,
	GetEmergencyAreas,
	GetInsuranceTypes,
	GetManagerDropDowns,
	GetMyAppointments,
	GetMyEmergencyInfo,
	GetMyHousing,
	GetMyPets,
	GetMyResidency,
	GetMyUser,
	GetPersonnel,
	GetUser,
	GetUserAppointments,
	GetUserDependents,
	GetUserEmergencyInfo,
	GetUserHousing,
	GetUserInsurance,
	GetUserPassports,
	GetUserPets,
	GetUserPhones,
	GetUserResidency,
	SaveUserAppointments,
	SaveUserBasics,
	SaveUserDependents,
	SaveUserEmergencyInfo,
	SaveUserGeneralInfo,
	SaveUserHousingInfo,
	SaveUserInsurance,
	SaveUserQatarInfo,
	SearchUsers,
	SetCurrentAppointment,
	SetUser,
	SetUserAppointments,
	SetUserEmergencyInfo,
	SetUserPets,
	SetUserResidency,
	UpdateApproverAssignments,
	UpdateCallingTreeAssignments,
} from './user.action';
import { UserStateModel } from './user.model';

export const DEFAULT_STATE: UserStateModel = {
	selectedUser: null,
	canArchive: false,
	isManagerOrApprover: false,
	isCTC: false,
	isDeanApprover: false,
	hasCrisisTeamRole: false,
	currentAppointment: null,
	managerDropDowns: [],
	branchMemberDropDowns: [],
	userPassports: [],
	userPhones: [],
	userHousing: null,
	userResidency: null,
	userPets: [],
	availableHousingUnits: [],
	userDependents: [],
	userAppointments: [],
	userPrimaryCoordinator: null,
	userBackupCoordinator: null,
	userUpcomingTravel: null,
	userOtherLanguages: [],
	userLocalContacts: [],
	userGlobalContacts: [],
	totalCallingTreeBranches: null,
	personnel: null,
	userInsurance: null,
	searchOptions: null,
	searchResults: null,
	searchForm: {
		model: {},
		dirty: false,
		status: '',
		errors: {},
	},
	insuranceTypes: [],
	emergencyAreas: [],
	approverAssignments: null,
	callingTreeAssignments: null,
};

@State<UserStateModel>({
	name: 'user',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class UserState {
	private basicBackend = inject(BasicBackendService);
	private hrBackend = inject(HrBackendService);
	private myBackend = inject(MyBackendService);
	private dashboardBackend = inject(DashboardBackendService);
	private store = inject(Store);
	private routerService = inject(RouterService);
	private generalBackend = inject(GeneralBackendService);
	private userBackend = inject(UserBackendService);
	private crisisTeamBackend = inject(CrisisTeamBackendService);
	private housingBackend = inject(HousingBackendService);

	get myRecord(): boolean {
		return this.routerService.isMyRecord();
	}

	@Selector()
	static userState(state: UserStateModel): UserStateModel {
		return state;
	}

	@Selector()
	static selectedUser(state: UserStateModel): User {
		return state.selectedUser;
	}

	@Selector()
	static canArchive(state: UserStateModel): boolean {
		return state.canArchive;
	}

	@Selector()
	static isManagerOrApprover(state: UserStateModel): boolean {
		return state.isManagerOrApprover;
	}

	@Selector()
	static isCTC(state: UserStateModel): boolean {
		return state.isCTC;
	}

	@Selector()
	static isDeanApprover(state: UserStateModel): boolean {
		return state.isDeanApprover;
	}

	@Selector()
	static hasCrisisTeamRole(state: UserStateModel): boolean {
		return state.hasCrisisTeamRole;
	}

	@Selector([UserState, AuthState])
	static isMyAccount(state: UserStateModel, auth: AuthStateModel): boolean {
		return auth.userId === state.selectedUser?.id;
	}

	@Selector([UserState, AuthState])
	static canReassignCTC(
		state: UserStateModel,
		auth: AuthStateModel
	): boolean {
		return AuthState.isCrisisTeam(auth) && state.isCTC;
	}

	@Selector([UserState, AuthState])
	static canReassignDeanApprover(
		state: UserStateModel,
		auth: AuthStateModel
	): boolean {
		return AuthState.isAdmin(auth) && state.isDeanApprover;
	}

	@Selector([UserState, AuthState])
	static canReassignCrisisTeamRole(
		state: UserStateModel,
		auth: AuthStateModel
	): boolean {
		return AuthState.isCrisisTeam(auth) && state.hasCrisisTeamRole;
	}

	@Selector()
	static currentAppointment(state: UserStateModel): Appointment {
		return state.currentAppointment;
	}

	@Selector()
	static managerDropDowns(state: UserStateModel): UserDropDown[] {
		return state.managerDropDowns;
	}

	@Selector()
	static managerIds(state: UserStateModel): number[] {
		return state.managerDropDowns?.map((manager) => manager.id);
	}

	@Selector()
	static branchMemberDropDowns(state: UserStateModel): UserDropDown[] {
		return state.branchMemberDropDowns;
	}

	@Selector()
	static branchMemberIds(state: UserStateModel): number[] {
		return state.branchMemberDropDowns?.map((member) => member.id);
	}

	@Selector()
	static userPassports(state: UserStateModel): UserPassport[] {
		return state.userPassports;
	}

	@Selector()
	static userPhones(state: UserStateModel): UserPhoneInfo[] {
		return state.userPhones;
	}

	@Selector()
	static userHousing(state: UserStateModel): Housing {
		return state.userHousing;
	}

	@Selector()
	static userResidency(state: UserStateModel): Residency {
		return state.userResidency;
	}

	@Selector()
	static userPets(state: UserStateModel): Pet[] {
		return state.userPets;
	}

	@Selector()
	static availableHousingUnits(state: UserStateModel): HousingUnit[] {
		return state.availableHousingUnits;
	}

	@Selector()
	static userDependents(state: UserStateModel): Dependent[] {
		return state.userDependents;
	}

	@Selector()
	static userAppointments(state: UserStateModel): Appointment[] {
		return state.userAppointments;
	}

	@Selector()
	static userPrimaryCoordinator(
		state: UserStateModel
	): CallingTreeCoordinator {
		return state.userPrimaryCoordinator;
	}

	@Selector()
	static userBackupCoordinator(
		state: UserStateModel
	): CallingTreeCoordinator {
		return state.userBackupCoordinator;
	}

	@Selector()
	static userUpcomingTravel(state: UserStateModel): TravelItinerary {
		return state.userUpcomingTravel;
	}

	@Selector()
	static userOtherLanguages(state: UserStateModel): Language[] {
		return state.userOtherLanguages;
	}

	@Selector()
	static userLocalContacts(state: UserStateModel): EmergencyContact[] {
		return state.userLocalContacts;
	}

	@Selector()
	static userGlobalContacts(state: UserStateModel): EmergencyContact[] {
		return state.userGlobalContacts;
	}

	@Selector()
	static totalCallingTreeBranches(state: UserStateModel): number {
		return state.totalCallingTreeBranches;
	}

	@Selector()
	static callingTreeBranchNumbers(state: UserStateModel): number[] {
		return state.totalCallingTreeBranches
			? MathUtility.arrayOfNumbers(state.totalCallingTreeBranches)
			: [];
	}

	@Selector()
	static personnel(state: UserStateModel): PersonnelStatus[] {
		return state.personnel;
	}

	@Selector()
	static userInsurance(state: UserStateModel): Insurance {
		return state.userInsurance;
	}

	@Selector()
	static searchResults(state: UserStateModel): UserSummary[] {
		return state.searchResults;
	}

	@Selector()
	static searchForm(state: UserStateModel): NgxsForm {
		return state.searchForm;
	}

	@Selector()
	static insuranceTypes(state: UserStateModel): DropDownItem[] {
		return state.insuranceTypes;
	}

	@Selector()
	static emergencyAreas(state: UserStateModel): DropDownItem[] {
		return state.emergencyAreas;
	}

	@Selector()
	static approverAssignments(state: UserStateModel): ApproverAssignment[] {
		return state.approverAssignments;
	}

	@Selector()
	static callingTreeAssignments(
		state: UserStateModel
	): CallingTreeAssignments {
		return state.callingTreeAssignments;
	}

	@Action(ClearUser)
	clearUser(ctx: StateContext<UserStateModel>, _action: ClearUser) {
		ctx.patchState(DEFAULT_STATE);
	}

	@Action(GetUser)
	getUser(ctx: StateContext<UserStateModel>, action: GetUser) {
		this.clearUser(ctx, new ClearUser());
		ctx.dispatch([new StartLoading()]);
		return this.userBackend.user(action.userId).pipe(
			tap((response) => {
				ctx.patchState({
					selectedUser: User.fromJSON(response.user),
					canArchive: response.canArchive,
					isManagerOrApprover: response.isManagerOrApprover,
					isCTC: response.isCTC,
					isDeanApprover: response.isDeanApprover,
					hasCrisisTeamRole: response.hasCrisisTeamRole,
				});
				ctx.dispatch([new StopLoading()]);
			})
		);
	}

	@Action(SetUser)
	setUser(ctx: StateContext<UserStateModel>, action: SetUser) {
		ctx.patchState({
			selectedUser: action.user,
		});
	}

	@Action(ArchiveUser)
	archiveUser(ctx: StateContext<UserStateModel>, action: ArchiveUser) {
		ctx.dispatch(new StartLoading());
		return this.getArchiveBackendCall(action.userId).pipe(
			tap((user) => ctx.dispatch([new SetUser(user), new StopLoading()])),
			catchError((_error) => {
				ctx.dispatch([new StopLoading()]);
				return of({});
			})
		);
	}

	private getArchiveBackendCall(userId: number): Observable<User> {
		return this.store.selectSnapshot(AuthState.isHR)
			? this.hrBackend.archiveUser(userId)
			: this.basicBackend.archiveUser(userId);
	}

	@Action(ActivateUser)
	activateUser(ctx: StateContext<UserStateModel>, action: ActivateUser) {
		ctx.dispatch(new StartLoading());
		return this.getActivateBackendCall(action.userId).pipe(
			tap((user) => ctx.dispatch([new SetUser(user), new StopLoading()])),
			catchError((_error) => {
				ctx.dispatch([new StopLoading()]);
				return of({});
			})
		);
	}

	private getActivateBackendCall(userId: number): Observable<User> {
		return this.store.selectSnapshot(AuthState.isHR)
			? this.hrBackend.activateUser(userId)
			: this.basicBackend.activateUser(userId);
	}

	@Action(GetApproverAssignments)
	getApproverAssignments(
		ctx: StateContext<UserStateModel>,
		_action: GetApproverAssignments
	) {
		ctx.patchState({
			approverAssignments: null,
		});
		return this.userBackend
			.getApproverAssignments(ctx.getState().selectedUser?.id)
			.pipe(
				tap((response) =>
					ctx.patchState({
						managerDropDowns:
							response.dropdowns?.map((dd) =>
								UserDropDown.fromJSON(dd)
							) ?? [],
						approverAssignments:
							response.approverAssignments?.map((ar) =>
								ApproverAssignment.fromJSON(ar)
							) ?? [],
					})
				)
			);
	}

	@Action(UpdateApproverAssignments)
	updateApproverAssignments(
		ctx: StateContext<UserStateModel>,
		action: UpdateApproverAssignments
	) {
		ctx.dispatch([new StartLoading()]);
		return this.userBackend
			.updateApproverAssignments(action.assignments)
			.pipe(
				tap((_response) => {
					ctx.patchState({
						isManagerOrApprover: false,
					});
					ctx.dispatch(new StopLoading());
				}),
				catchError((error) => {
					ctx.dispatch(new StopLoading());
					return throwError(() => error);
				})
			);
	}

	@Action(GetCallingTreeAssignments)
	getCallingTreeAssignments(
		ctx: StateContext<UserStateModel>,
		_action: GetCallingTreeAssignments
	) {
		ctx.patchState({
			callingTreeAssignments: null,
			branchMemberDropDowns: null,
		});
		return this.userBackend
			.getCallingTreeAssignments(ctx.getState().selectedUser?.id)
			.pipe(
				tap((response) =>
					ctx.patchState({
						branchMemberDropDowns:
							response.dropdowns?.map((dd) =>
								UserDropDown.fromJSON(dd)
							) ?? [],
						callingTreeAssignments: response.assignments
							? CallingTreeAssignments.fromJSON(
									response.assignments
								)
							: null,
					})
				)
			);
	}

	@Action(UpdateCallingTreeAssignments)
	updateCallingTreeAssignments(
		ctx: StateContext<UserStateModel>,
		action: UpdateCallingTreeAssignments
	) {
		ctx.dispatch([new StartLoading()]);
		return this.userBackend
			.updateCallingTreeAssignments(action.assignments)
			.pipe(
				tap((_response) => {
					ctx.patchState({
						isCTC: false,
					});
					ctx.dispatch(new StopLoading());
				}),
				catchError((error) => {
					ctx.dispatch(new StopLoading());
					return throwError(() => error);
				})
			);
	}

	@Action(GetCurrentAppointment)
	getCurrentAppointment(
		ctx: StateContext<UserStateModel>,
		action: GetCurrentAppointment
	) {
		ctx.patchState({
			currentAppointment: null,
		});
		ctx.dispatch([new StartLoading()]);
		return this.basicBackend.currentAppointment(action.userId).pipe(
			tap((appointment) => {
				ctx.dispatch([
					new SetCurrentAppointment(appointment),
					new StopLoading(),
				]);
			})
		);
	}

	@Action(SetCurrentAppointment)
	setCurrentAppointment(
		ctx: StateContext<UserStateModel>,
		action: SetCurrentAppointment
	) {
		ctx.patchState({
			currentAppointment: action.appointment,
		});
	}

	@Action(GetManagerDropDowns)
	getManagerDropDowns(
		ctx: StateContext<UserStateModel>,
		_action: GetManagerDropDowns
	) {
		return this.userBackend
			.getManagerDropDowns()
			.pipe(
				tap((dropDowns) =>
					ctx.patchState({ managerDropDowns: dropDowns })
				)
			);
	}

	@Action(SaveUserGeneralInfo)
	saveUserGeneralInfo(
		ctx: StateContext<UserStateModel>,
		action: SaveUserGeneralInfo
	) {
		ctx.dispatch([new StartLoading()]);
		return this.basicBackend
			.saveUserGeneralInfo(
				action.user,
				action.appointment,
				action.passports,
				action.phoneNumbers
			)
			.pipe(
				tap((response) => {
					ctx.patchState({
						selectedUser: User.fromJSON(response.user),
					});
					ctx.dispatch([new StopLoading()]);
				}),
				catchError((error) => {
					ctx.dispatch([new StopLoading()]);
					return throwError(() => error);
				})
			);
	}

	@Action(GetUserPassports)
	getUserPassports(
		ctx: StateContext<UserStateModel>,
		action: GetUserPassports
	) {
		return this.getUserPassportsBackendCall(action.userId).pipe(
			tap((passports) => ctx.patchState({ userPassports: passports }))
		);
	}

	private getUserPassportsBackendCall(
		userId: number
	): Observable<UserPassport[]> {
		return this.store.selectSnapshot(AuthState.hasUserResource)
			? this.userBackend.userPassports(userId)
			: this.myBackend.userPassports(userId);
	}

	/**
	 * GetUserPhones: fetches selected User Phones from backend and updates state
	 */
	@Action(GetUserPhones)
	getUserPhones(ctx: StateContext<UserStateModel>, action: GetUserPhones) {
		return this.getUserPhonesBackendCall(action.userId).pipe(
			tap((phones) => {
				ctx.patchState({ userPhones: phones });
			})
		);
	}

	private getUserPhonesBackendCall(
		userId: number
	): Observable<UserPhoneInfo[]> {
		return this.store.selectSnapshot(AuthState.hasUserResource)
			? this.userBackend.userPhoneInfo(userId)
			: this.myBackend.userPhoneInfo(userId);
	}

	@Action(GetUserHousing)
	getUserHousing(ctx: StateContext<UserStateModel>, action: GetUserHousing) {
		return this.userBackend.userHousing(action.userId).pipe(
			tap((housing) => {
				ctx.patchState({ userHousing: housing });
			})
		);
	}

	@Action(GetAvailableHousingUnits)
	getAvailableHousingUnits(
		ctx: StateContext<UserStateModel>,
		action: GetAvailableHousingUnits
	) {
		return this.userBackend
			.getAvailableHousingUnits(action.userId)
			.pipe(
				tap((units) => ctx.patchState({ availableHousingUnits: units }))
			);
	}

	@Action(SaveUserHousingInfo)
	saveUserHousingInfo(
		ctx: StateContext<UserStateModel>,
		action: SaveUserHousingInfo
	) {
		return this.basicBackend
			.saveUserHousingInfo(action.userId, action.housing)
			.pipe(tap(() => ctx.dispatch([new GetUserHousing(action.userId)])));
	}

	@Action(GetUserDependents)
	getUserDependents(
		ctx: StateContext<UserStateModel>,
		action: GetUserDependents
	) {
		return this.getUserDependentsBackendCall(action.userId).pipe(
			tap((dependents) => {
				ctx.patchState({ userDependents: dependents });
			})
		);
	}

	private getUserDependentsBackendCall(
		userId: number
	): Observable<Dependent[]> {
		return this.myRecord
			? this.myBackend.dependents()
			: this.userBackend.userDependents(userId);
	}

	@Action(SaveUserDependents)
	saveUserDependents(
		ctx: StateContext<UserStateModel>,
		action: SaveUserDependents
	) {
		ctx.dispatch([new StartLoading()]);
		return this.getSaveUserDependentsBackendCall(
			action.userId,
			action.dependents
		).pipe(
			tap(() => {
				ctx.dispatch([
					new GetUserDependents(action.userId),
					new StopLoading(),
				]);
			}),
			catchError((error) => {
				ctx.dispatch(new StopLoading());
				return throwError(() => error);
			})
		);
	}

	private getSaveUserDependentsBackendCall(
		userId: number,
		dependents: Dependent[]
	): Observable<BaseResponse> {
		return this.store.selectSnapshot(AuthState.isHR)
			? this.hrBackend.saveUserDependents(userId, dependents)
			: this.basicBackend.saveUserDependents(userId, dependents);
	}

	@Action(GetMyUser)
	getMyUser(ctx: StateContext<UserStateModel>, _action: GetMyUser) {
		ctx.patchState(DEFAULT_STATE);
		ctx.dispatch([new StartLoading()]);
		return this.myBackend.user().pipe(
			tap((response) => {
				ctx.patchState({
					selectedUser: User.fromJSON(response.user),
					canArchive: false,
					isManagerOrApprover: response.isManagerOrApprover,
					isCTC: response.isCTC,
					isDeanApprover: response.isDeanApprover,
					hasCrisisTeamRole: response.hasCrisisTeamRole,
				});
				ctx.dispatch([new StopLoading()]);
			})
		);
	}

	@Action(ConfirmMyRecord)
	confirmMyRecord(
		ctx: StateContext<UserStateModel>,
		_action: ConfirmMyRecord
	) {
		ctx.dispatch([new StartLoading()]);
		return this.myBackend.confirm().pipe(
			tap((response) => {
				ctx.patchState({
					selectedUser: User.fromJSON(response.user),
				});
				ctx.dispatch([new StopLoading()]);
			})
		);
	}

	@Action(GetMyAppointments)
	getMyAppointments(
		ctx: StateContext<UserStateModel>,
		_action: GetMyAppointments
	) {
		return this.myBackend
			.appointments()
			.pipe(
				tap((appointments) =>
					ctx.patchState({ userAppointments: appointments })
				)
			);
	}

	@Action(SetUserAppointments)
	setUserAppointments(
		ctx: StateContext<UserStateModel>,
		action: SetUserAppointments
	) {
		ctx.patchState({ userAppointments: action.appointments });
	}

	@Action(GetMyResidency)
	getMyResidency(ctx: StateContext<UserStateModel>, _action: GetMyResidency) {
		return this.myBackend
			.residency()
			.pipe(
				tap((residency) => ctx.patchState({ userResidency: residency }))
			);
	}

	@Action(SetUserResidency)
	setUserResidency(
		ctx: StateContext<UserStateModel>,
		action: SetUserResidency
	) {
		ctx.patchState({ userResidency: action.residency });
	}

	@Action(GetMyHousing)
	getMyHousing(ctx: StateContext<UserStateModel>, _action: GetMyHousing) {
		return this.myBackend
			.housing()
			.pipe(tap((housing) => ctx.patchState({ userHousing: housing })));
	}

	@Action(GetMyPets)
	getMyPets(ctx: StateContext<UserStateModel>, _action: GetMyPets) {
		return this.myBackend
			.pets()
			.pipe(tap((pets) => ctx.patchState({ userPets: pets })));
	}

	@Action(SetUserPets)
	setUserPets(ctx: StateContext<UserStateModel>, action: SetUserPets) {
		ctx.patchState({ userPets: action.pets });
	}

	@Action(SaveUserQatarInfo)
	saveUserQatarInfo(
		ctx: StateContext<UserStateModel>,
		action: SaveUserQatarInfo
	) {
		ctx.dispatch([new StartLoading()]);
		return this.getSaveUserQatarInfoBackendCall(action.userQatarInfo).pipe(
			tap(() => ctx.dispatch(new StopLoading())),
			catchError((error) => {
				ctx.dispatch(new StopLoading());
				return throwError(() => error);
			})
		);
	}

	private getSaveUserQatarInfoBackendCall(
		userQatarInfo: UserQatarInfo
	): Observable<BaseResponse> {
		return this.myRecord
			? this.myBackend.saveQatarInfo(userQatarInfo)
			: this.store.selectSnapshot(AuthState.isHR)
				? this.hrBackend.saveUserQatarInfo(userQatarInfo)
				: this.housingBackend.saveUserHousingInfo(
						userQatarInfo.id,
						userQatarInfo.housing
					);
	}

	@Action(GetMyEmergencyInfo)
	getMyEmergencyInfo(
		ctx: StateContext<UserStateModel>,
		_action: GetMyEmergencyInfo
	) {
		return this.myBackend
			.emergencyInfo()
			.pipe(
				tap((response) =>
					ctx.dispatch(new SetUserEmergencyInfo(response))
				)
			);
	}

	@Action(SetUserEmergencyInfo)
	setUserEmergencyInfo(
		ctx: StateContext<UserStateModel>,
		action: SetUserEmergencyInfo
	) {
		ctx.patchState({
			userPrimaryCoordinator: action.response.primaryCTC
				? CallingTreeCoordinator.fromJSON(action.response.primaryCTC)
				: null,
			userBackupCoordinator: action.response.backupCTC
				? CallingTreeCoordinator.fromJSON(action.response.backupCTC)
				: null,
			userUpcomingTravel: action.response.upcomingTravel
				? TravelItinerary.fromJSON(action.response.upcomingTravel)
				: null,
			userOtherLanguages: action.response.otherLanguages?.map(
				(languageJSON) => Language.fromJSON(languageJSON)
			),
			userLocalContacts: action.response.localContacts?.map((localJSON) =>
				EmergencyContact.fromJSON(localJSON)
			),
			userGlobalContacts: action.response.globalContacts?.map(
				(globalJSON) => EmergencyContact.fromJSON(globalJSON)
			),
			totalCallingTreeBranches: action.response.totalCallingTreeBranches,
		});
	}

	@Action(SaveUserEmergencyInfo)
	saveUserEmergencyInfo(
		ctx: StateContext<UserStateModel>,
		action: SaveUserEmergencyInfo
	) {
		ctx.dispatch([new StartLoading()]);
		return this.getSaveUserEmergencyInfoBackendCall(
			action.emergencyInfo
		).pipe(
			tap((user) => {
				const actions = this.myRecord
					? [
							new SetUser(user),
							new StopLoading(),
							new GetMyEmergencyInfo(),
						]
					: [
							new SetUser(user),
							new StopLoading(),
							new GetUserEmergencyInfo(action.emergencyInfo.id),
						];
				ctx.dispatch(actions);
			}),
			catchError((error) => {
				ctx.dispatch(new StopLoading());
				return throwError(() => error);
			})
		);
	}

	private getSaveUserEmergencyInfoBackendCall(
		emergencyInfo: EmergencyInfo
	): Observable<User> {
		return this.myRecord
			? this.myBackend.saveEmergencyInfo(emergencyInfo)
			: this.store.selectSnapshot(AuthState.isCrisisTeam)
				? this.crisisTeamBackend.saveUserEmergencyInfo(emergencyInfo)
				: this.hrBackend.saveUserEmergencyInfo(emergencyInfo);
	}

	@Action(GetPersonnel)
	getPersonnel(ctx: StateContext<UserStateModel>, _action: GetPersonnel) {
		return this.dashboardBackend.getPersonnel().pipe(
			tap((personnel) =>
				ctx.patchState({
					personnel: personnel,
				})
			)
		);
	}

	/**
	 * GetUserAppointments: fetches selected User Appointments from backend and updates state
	 */
	@Action(GetUserAppointments)
	getUserAppointments(
		ctx: StateContext<UserStateModel>,
		action: GetUserAppointments
	) {
		return this.userBackend.userAppointments(action.userId).pipe(
			tap((appointments) => {
				ctx.dispatch(new SetUserAppointments(appointments));
			})
		);
	}

	/**
	 * GetUserResidency: fetches selected User Residency from backend and updates state
	 */
	@Action(GetUserResidency)
	getUserResidency(
		ctx: StateContext<UserStateModel>,
		action: GetUserResidency
	) {
		return this.userBackend
			.userResidency(action.userId)
			.pipe(
				tap((residency) =>
					ctx.dispatch(new SetUserResidency(residency))
				)
			);
	}

	/**
	 * GetUserPets: fetches selected User Pets from backend and updates state
	 */
	@Action(GetUserPets)
	getUserPets(ctx: StateContext<UserStateModel>, action: GetUserPets) {
		return this.userBackend
			.userPets(action.userId)
			.pipe(tap((pets) => ctx.dispatch(new SetUserPets(pets))));
	}

	/**
	 * GetUserInsurance: fetches selected User's Insurance Info from backend and updates state
	 */
	@Action(GetUserInsurance)
	getUserInsurance(
		ctx: StateContext<UserStateModel>,
		action: GetUserInsurance
	) {
		return this.userBackend.userInsurance(action.userId).pipe(
			tap((insurance) => {
				ctx.patchState({ userInsurance: insurance });
			})
		);
	}

	/**
	 * GetUserEmergencyInfo: fetches selected User's Emergency Information and updates state
	 */
	@Action(GetUserEmergencyInfo)
	getUserEmergencyInfo(
		ctx: StateContext<UserStateModel>,
		action: GetUserEmergencyInfo
	) {
		return this.userBackend
			.userEmergencyInfo(action.userId)
			.pipe(
				tap((response) =>
					ctx.dispatch(new SetUserEmergencyInfo(response))
				)
			);
	}

	/**
	 * SearchUsers: Search for Users
	 */
	@Action(SearchUsers)
	search(ctx: StateContext<UserStateModel>, action: SearchUsers) {
		ctx.dispatch(new StartLoading());
		ctx.patchState({ searchOptions: action.options });
		return this.userBackend.search(action.options).pipe(
			tap((response: UserSearchResponse) => {
				ctx.patchState({
					searchResults: response.searchResult?.map((result) =>
						UserSummary.fromJSON(result)
					),
					searchOptions: UserSearch.fromJSON(response.options),
				});
				ctx.dispatch(new StopLoading());
			})
		);
	}

	/**
	 * ClearUserSearchResult: Sets the searchResults to null
	 */
	@Action(ClearUserSearchResult)
	clearUserSearchResult(
		ctx: StateContext<UserStateModel>,
		_action: ClearUserSearchResult
	) {
		ctx.patchState({ searchResults: null });
	}

	/**
	 * GetInsuranceTypes: Fetches Insurance Type drop-down values from server and populates `insuranceTypes` state property
	 */
	@Action(GetInsuranceTypes)
	getInsuranceTypes(
		ctx: StateContext<UserStateModel>,
		_action: GetInsuranceTypes
	) {
		return this.generalBackend
			.getInsuranceTypes()
			.pipe(
				tap((insuranceTypes) =>
					ctx.patchState({ insuranceTypes: insuranceTypes })
				)
			);
	}

	/**
	 * GetEmergencyAreas: Fetches Emergency Area drop-down values from server and populates `emergencyAreas` state property
	 */
	@Action(GetEmergencyAreas)
	getEmergencyAreas(
		ctx: StateContext<UserStateModel>,
		_action: GetEmergencyAreas
	) {
		return this.generalBackend
			.getEmergencyAreas()
			.pipe(tap((areas) => ctx.patchState({ emergencyAreas: areas })));
	}

	/**
	 * SaveUserBasics: Save the User Basics part of the form, then updates the selected User
	 */
	@Action(SaveUserBasics)
	saveUserBasics(ctx: StateContext<UserStateModel>, action: SaveUserBasics) {
		ctx.dispatch([new StartLoading()]);
		return this.hrBackend.saveUserBasics(action.userBasics).pipe(
			tap((user) => ctx.dispatch([new SetUser(user), new StopLoading()])),
			catchError((error) => {
				ctx.dispatch(new StopLoading());
				return throwError(() => error);
			})
		);
	}

	/**
	 * SaveUserAppointments: Save the User Appointments part of the form, then updates the userAppointments state property
	 */
	@Action(SaveUserAppointments)
	saveUserAppointments(
		ctx: StateContext<UserStateModel>,
		action: SaveUserAppointments
	) {
		ctx.dispatch([new StartLoading()]);
		return this.hrBackend
			.saveUserAppointments(action.userId, action.appointments)
			.pipe(
				tap((appointments) =>
					ctx.dispatch([
						new SetUserAppointments(appointments),
						new StopLoading(),
					])
				),
				catchError((error) => {
					ctx.dispatch(new StopLoading());
					return throwError(() => error);
				})
			);
	}

	/**
	 * SaveUserInsurance
	 */
	@Action(SaveUserInsurance)
	saveUserInsurance(
		ctx: StateContext<UserStateModel>,
		action: SaveUserInsurance
	) {
		ctx.dispatch([new StartLoading()]);
		return this.hrBackend
			.saveUserInsurance(action.userId, action.insurance)
			.pipe(
				tap((_response) => {
					ctx.dispatch([
						new StopLoading(),
						new GetUserInsurance(action.userId),
					]);
				}),
				catchError((error) => {
					ctx.dispatch(new StopLoading());
					return throwError(() => error);
				})
			);
	}

	@Action(Logout)
	logout(ctx: StateContext<UserStateModel>, _action: Logout) {
		ctx.patchState(DEFAULT_STATE);
	}
}
